import { CheckCircle2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi } from '@/lib/auth-api';
import { getErrorMessage, getFieldErrors } from '@/lib/errors';
import { GoogleSignInButton } from '@/modules/auth/GoogleSignInButton';
import { TRIAL } from '@/modules/marketing/pricing-data';
import { usePageMeta } from '@/lib/seo';
import { PAGES } from '@/seo/pages';

export function RegisterPage() {
  usePageMeta(PAGES.register);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (password !== confirm) {
      setFieldErrors({ password: 'Passwords do not match.' });
      return;
    }

    setLoading(true);
    try {
      await authApi.register(email, password, firstName, lastName, company);
      setDone(true);
    } catch (err) {
      setError(getErrorMessage(err));
      setFieldErrors(getFieldErrors(err));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title="Check your email" subtitle="One more step to activate your account">
        <div className="flex flex-col items-center gap-3 text-center">
          <CheckCircle2 className="size-10 text-success" />
          {/* The trial starts when the address is confirmed, not now, so this is the moment to say
              so: somebody who never clicks the link has not started anything. */}
          <p className="text-sm text-muted-foreground">
            We've sent a verification link to <span className="font-medium text-foreground">{email}</span>.
            Click it to activate your account and start your {TRIAL.days}-day free trial, then sign in.
          </p>
          <Button asChild variant="outline" className="mt-2 w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle={`Start a ${TRIAL.days}-day free trial of automation, monitoring and backups. No card needed.`}
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Two name fields, one stored string.
            Two on the form because an administrator looking at a customer list wants to sort by
            family name, and a single field cannot be split back reliably. One string in storage
            because required name *parts* break for people with one name, three names, or whose
            family name comes first — so the split lives on the form and never in the database. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="first-name">First name</Label>
            <Input id="first-name" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
            {fieldErrors.first_name && <p className="text-xs text-danger">{fieldErrors.first_name}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="last-name">Last name</Label>
            <Input id="last-name" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            {fieldErrors.last_name && <p className="text-xs text-danger">{fieldErrors.last_name}</p>}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">
            Company <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Input id="company" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} />
          {fieldErrors.company && <p className="text-xs text-danger">{fieldErrors.company}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          {fieldErrors.email && <p className="text-xs text-danger">{fieldErrors.email}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          {fieldErrors.password ? (
            <p className="text-xs text-danger">{fieldErrors.password}</p>
          ) : (
            <p className="text-xs text-muted-foreground">At least 12 characters, with upper, lower, and a number.</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading && <Loader2 className="animate-spin" />} Create account
        </Button>

        {/* The same button as on sign-in, deliberately. Google has already proved the address, so
            there is nothing to verify and no second form to fill in. Making people choose between
            "sign up" and "sign in" only creates a wrong answer to recover from. */}
        <GoogleSignInButton label="Continue with Google" />
      </form>
    </AuthLayout>
  );
}
